import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";

import { ApiError, api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { Logo } from "@/components/Logo";

/**
 * Public self-service sign-up page.
 *
 * Anyone with the URL can submit `email + password + (optional) note`,
 * which lands in `membership_requests` as a `pending` row. An existing
 * admin then approves it from the Teams page, at which point the saved
 * password hash is promoted directly into the `users` table — no email
 * sender, no temporary password, the applicant signs in with what they
 * typed here.
 *
 * The page is mounted *outside* the AuthGate (see `router.tsx`), so a
 * browser without a session can reach it. If a session *does* exist we
 * just bounce to the dashboard — there is no point in applying when you
 * are already a member.
 */
export function Signup() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (user) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Password and confirmation do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post<{ status: string; message: string }>("/auth/signup", {
        email,
        password,
        note: note.trim() || null,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          (err.body as { detail?: string } | null)?.detail ?? `HTTP ${err.status}`;
        setError(detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Sign-up request failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <div className="auth-card__brand">
            <Logo size={28} />
            <div className="auth-card__title">
              Back<span className="accent">Stage</span>
            </div>
          </div>
          <div className="auth-banner auth-banner--ok" style={{ marginTop: 18 }}>
            Application submitted.
          </div>
          <p className="auth-card__sub" style={{ margin: "12px 0 18px" }}>
            An administrator will review your request. Once approved, you can
            sign in with the email and password you just set.
          </p>
          <div className="auth-card__footer">
            <Link to="/login">Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="auth-card__brand">
          <Logo size={28} />
          <div className="auth-card__title">
            Back<span className="accent">Stage</span>
          </div>
        </div>
        <p className="auth-card__sub">
          Apply to join. An existing administrator will approve your account.
        </p>

        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </label>
        <label className="auth-field">
          <span>Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
          />
        </label>
        <label className="auth-field">
          <span>Note for the admin (optional)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="why you need access"
            maxLength={2048}
          />
        </label>

        {error && <div className="auth-error">{error}</div>}

        <button
          type="submit"
          className="btn btn--primary auth-submit"
          disabled={submitting || !email || !password || !confirm}
        >
          {submitting ? "Submitting…" : "Apply for access"}
        </button>

        <div className="auth-card__footer">
          <Link to="/login">Already approved? Sign in</Link>
        </div>
      </form>
    </div>
  );
}
